import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';
import { useAuth as useClerkAuth, useOrganization } from '@clerk/clerk-react';
import { useAuth } from '@/components/auth/AuthProvider';
import type { Workspace } from '@/types/workspace';

let workspaceServicePromise: Promise<typeof import('@/services/workspaceService')> | null = null;
const getWorkspaceService = () => {
  if (!workspaceServicePromise) {
    workspaceServicePromise = import('@/services/workspaceService');
  }
  return workspaceServicePromise;
};

interface CreateWorkspacePayload {
  name: string;
  description?: string;
}

interface InvitePayload {
  email: string;
  workspaceId: string;
  role: 'admin' | 'editor' | 'viewer';
}

interface RemoveMemberPayload {
  workspaceId: string;
  userId: string;
}

interface UpdateRolePayload {
  workspaceId: string;
  userId: string;
  role: 'admin' | 'editor' | 'viewer';
}

interface UpdateWorkspacePayload {
  workspaceId: string;
  name?: string;
  description?: string;
}

export const useWorkspaces = () => {
  const queryClient = useQueryClient();
  const isUsingClerk = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  
  // Always call all hooks
  const clerkAuth = useClerkAuth();
  const { organization } = useOrganization();
  const supabaseAuth = useAuth();
  
  // Determine user and org IDs based on which system is in use
  const userId = isUsingClerk ? clerkAuth?.userId : supabaseAuth.user?.id;
  const orgId = isUsingClerk ? organization?.id : null;

  // Fetch workspaces list
  const {
    data: workspaces = [],
    isFetching: isLoading,
    refetch,
  } = useQuery<Workspace[]>({
    queryKey: ['workspaces', userId, orgId],
    queryFn: async () => {
      const { getWorkspaces } = await getWorkspaceService();
      return getWorkspaces(userId || undefined, orgId || undefined);
    },
    enabled: !!(userId || orgId),
  });

  // Create workspace
  const createWorkspaceMutation = useMutation({
    mutationFn: async ({ name, description }: CreateWorkspacePayload) => {
      const { createWorkspace } = await getWorkspaceService();
      return createWorkspace(name, description ?? null, userId || undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success('Workspace created');
    },
    onError: (err: unknown) => {
      logger.error('Create workspace error:', err);
      // Only show toast for non-RLS errors
      if (err && typeof err === 'object' && 'code' in err && err.code !== '42501') {
        toast.error('Failed to create workspace');
      }
    },
  });

  // Invite user
  const inviteUserMutation = useMutation({
    mutationFn: async (payload: InvitePayload) => {
      const { inviteUserToWorkspace } = await getWorkspaceService();
      return inviteUserToWorkspace(payload.email, payload.workspaceId, payload.role);
    },
    onSuccess: () => {
      toast.success('Invitation sent');
    },
    onError: (err: unknown) => {
      logger.error(err);
      toast.error('Failed to send invitation');
    },
  });

  // Remove member
  const removeMemberMutation = useMutation({
    mutationFn: async ({ workspaceId, userId }: RemoveMemberPayload) => {
      const { removeWorkspaceMember } = await getWorkspaceService();
      return removeWorkspaceMember(workspaceId, userId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspaceMembers', variables.workspaceId] });
      toast.success('Member removed');
    },
    onError: (err: unknown) => {
      logger.error(err);
      toast.error('Failed to remove member');
    },
  });

  // Update member role
  const updateRoleMutation = useMutation({
    mutationFn: async (payload: UpdateRolePayload) => {
      const { updateMemberRole } = await getWorkspaceService();
      return updateMemberRole(payload.workspaceId, payload.userId, payload.role);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspaceMembers', variables.workspaceId] });
      toast.success('Role updated');
    },
    onError: (err: unknown) => {
      logger.error(err);
      toast.error('Failed to update role');
    },
  });

  // Update workspace details
  const updateWorkspaceMutation = useMutation({
    mutationFn: async ({ workspaceId, ...updates }: UpdateWorkspacePayload) => {
      const { updateWorkspace } = await getWorkspaceService();
      return updateWorkspace(workspaceId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success('Workspace updated');
    },
    onError: (err: unknown) => {
      logger.error(err);
      toast.error('Failed to update workspace');
    },
  });

  return {
    workspaces,
    isLoading,
    refetch,
    createWorkspace: createWorkspaceMutation.mutate,
    createWorkspaceAsync: createWorkspaceMutation.mutateAsync,
    inviteUser: inviteUserMutation.mutate,
    removeMember: removeMemberMutation.mutate,
    updateMemberRole: updateRoleMutation.mutate,
    updateWorkspace: updateWorkspaceMutation.mutate,
    updateWorkspaceAsync: updateWorkspaceMutation.mutateAsync,
  } as const;
}; 
